/**
 * EL DETALLE DE CADA TARJETA DE LA FICHA UNICA (SUJ-T8 bloque 2; ajuste 1 de Josse, #17340).
 *
 * Cada tarjeta de "como va este mes" se abre en la MISMA ficha y pide su detalle SOLO al abrirse:
 *   asistencia -> los dias del detector (GET .../asistencia/anomalias, uno por negocio de sus fichas)
 *   danos      -> GET /api/v1/personas/{id}/danos      (nexo-api 308: DanoDeDiaDto, regla D-41)
 *   creditos   -> GET /api/v1/personas/{id}/creditos   (nexo-api 308: CreditosDePersonaDto por negocio)
 *
 * Aqui se lee la forma de cada respuesta y se decide que decir cuando no llega. Un 404 SIN cuerpo es
 * la ruta que aun no esta publicada: se dice asi y la tarjeta se enciende sola cuando exista.
 *
 * Sin DOM: lo prueba scripts/check-ficha-detalle.mjs.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const numero = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** La lista de una respuesta: el API la da sin sobre (Ok(list)) o dentro de { data }. */
export function listaDe(res) {
  const c = res?.data;
  if (Array.isArray(c)) return c;
  if (Array.isArray(c?.data)) return c.data;
  if (Array.isArray(c?.items)) return c.items;
  if (Array.isArray(c?.data?.items)) return c.data.items;
  return [];
}

/** Por que no hay detalle, dicho segun la causa. null si la respuesta sirve. */
export function porQueNoHayDetalle(res) {
  if (!res) return { motivo: 'RED', frase: 'No pudimos hablar con la API.' };
  if (res.ok) return null;
  if (res.code === 404 && !res.details) return { motivo: 'NO_PUBLICADO', frase: 'Este detalle se enciende solo cuando la API lo publique.' };
  if (res.code === 403) return { motivo: 'PERMISO', frase: 'Tu usuario no tiene permiso para ver este detalle.' };
  if (res.code === 401 || res.code === 'UNAUTHORIZED') return { motivo: 'SIN_SESION', frase: 'La sesión se cerró: vuelve a entrar.' };
  return { motivo: 'RED', frase: texto(res.details?.mensaje) || texto(res.error) || 'No pudimos hablar con la API.' };
}

const pesosCOP = (n) => '$' + Math.round(numero(n)).toLocaleString('es-CO');
const diaCorto = (v) => {
  const t = Date.parse(texto(v).length === 10 ? `${texto(v)}T12:00:00Z` : texto(v));
  return Number.isFinite(t) ? new Date(t).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', weekday: 'short', day: 'numeric', month: 'short' }) : texto(v);
};

/**
 * Los danos por dia: su parte de cada dano (regla D-41: repartido entre quienes trabajaron en el area).
 * @returns {{ filas: Array<{dia:string, negocio:string, insumo:string, cantidad:number, pesosDelDano:number, suParte:number, entre:number}>, total:number }}
 */
export function danosPorDia(res, negocioDe = (id) => id) {
  const filas = listaDe(res).map((d) => {
    const frac = numero(d.suFraccion ?? d.su_fraccion);
    return {
      dia: diaCorto(d.dia),
      orden: texto(d.dia),
      negocio: negocioDe(texto(d.businessId ?? d.business_id)),
      insumo: texto(d.insumo) || 'insumo',
      cantidad: numero(d.cantidadDelDano ?? d.cantidad_del_dano),
      pesosDelDano: numero(d.pesosDelDano ?? d.pesos_del_dano),
      suParte: numero(d.suParte ?? d.su_parte),
      entre: frac > 0 ? Math.round(1 / frac) : 0
    };
  }).sort((a, b) => (a.orden < b.orden ? 1 : a.orden > b.orden ? -1 : 0));
  return { filas, total: filas.reduce((a, f) => a + f.suParte, 0) };
}

/**
 * Los creditos por negocio. La deuda CONFIRMADA (documento o nombre) va aparte de la POSIBLE (solo
 * telefono); un credito con sobrepago (abonos inflados, #17434) no suma a la deuda y se marca.
 */
export function creditosPorNegocio(res, negocioDe = (id) => id) {
  const negocios = listaDe(res).map((n) => ({
    negocio: negocioDe(texto(n.businessId ?? n.business_id)),
    confirmada: numero(n.deudaConfirmada ?? n.deuda_confirmada),
    posible: numero(n.deudaPosible ?? n.deuda_posible),
    conSaldo: numero(n.creditosConSaldoConfirmados ?? n.creditos_con_saldo_confirmados),
    conSaldoPosibles: numero(n.creditosConSaldoPosibles ?? n.creditos_con_saldo_posibles),
    creditos: (Array.isArray(n.items) ? n.items : []).map((c) => ({
      fecha: diaCorto(c.fecha),
      factura: texto(c.numeroFactura ?? c.numero_factura),
      total: numero(c.total), abonado: numero(c.abonado), saldo: numero(c.saldo),
      sobrepago: c.sobrepago === true,
      posible: texto(c.fuente).toLowerCase() === 'telefono',
      abonos: (Array.isArray(c.abonos) ? c.abonos : []).map((a) => ({ fecha: diaCorto(a?.fecha ?? a?.Fecha), monto: numero(a?.monto ?? a?.Monto ?? a?.valor) }))
    }))
  }));
  return {
    negocios,
    confirmada: negocios.reduce((a, n) => a + n.confirmada, 0),
    posible: negocios.reduce((a, n) => a + n.posible, 0)
  };
}

/** El titular de la tarjeta de creditos, ya con el detalle leido. */
export function titularDeCreditos(c) {
  if (c.confirmada <= 0 && c.posible <= 0) return { valor: pesosCOP(0), detalle: 'Sin saldo pendiente' };
  return {
    valor: pesosCOP(c.confirmada),
    detalle: c.posible > 0 ? `y ${pesosCOP(c.posible)} posibles (solo cruzan por teléfono, sin confirmar)` : 'confirmados por documento o nombre'
  };
}

/** Los dias del detector de ESTA ficha, del mas reciente al mas viejo. */
export function diasDeAsistencia(filas) {
  return (Array.isArray(filas) ? filas : [])
    .map((f) => ({
      orden: texto(f.fecha),
      dia: diaCorto(f.fecha),
      clase: texto(f.clasificacion).toUpperCase() || 'SIN_CLASIFICAR',
      entrada: texto(f.entradaReal ?? f.entrada_real),
      desvio: numero(f.desvioMin ?? f.desvio_min)
    }))
    .sort((a, b) => (a.orden < b.orden ? 1 : a.orden > b.orden ? -1 : 0));
}

/** La clasificacion del detector dicha en palabras. */
export function claseEnPalabras(clase, desvio = 0) {
  const c = String(clase || '').toUpperCase();
  if (c === 'OK') return 'Sin novedad';
  if (c === 'TARDANZA') return `Tardanza ${desvio} min`;
  if (c === 'DIA_INCOMPLETO') return 'Día incompleto';
  if (c === 'DIA_EN_CURSO') return 'En curso';
  if (c === 'TRABAJO_EN_DESCANSO') return 'Trabajó en su descanso';
  if (c === 'POSIBLE_INTERCAMBIO') return 'Posible cambio de turno';
  if (c === 'MARCAJE_SOSPECHOSO') return 'Marcaje para revisar';
  if (c === 'FUERA_DE_RANGO') return 'Fuera de su horario';
  return c.replace(/_/g, ' ').toLowerCase();
}

export { pesosCOP };
